import { Table as BaseTable, TableProps } from '@/components/table'
import { Query } from './table_query'
import { EventContactsQuery } from './__generated__/table_query'
import { useQuery } from '@apollo/client'
import { Inline as Error } from '@/components/error'
import { Inline as Loading } from '@/components/loading'
import { useState } from 'react'
import { Badge } from '@/components/event_contact_type/badge'
import { EventContactFilters } from '@/types'
import { contactTypes } from './contact_types'
import { useTable } from '@/helpers/useTable'
import { Button } from '../base/button'
import { dedupe } from '@/helpers/dedupe'
import { EditDialog } from './edit_dialog'
import { NewDialog } from './new_dialog'

interface Props extends TableProps {
  eventId: string
  createable?: boolean
  updateable?: boolean
}

export const Table = ({ pageSize = 10, eventId, createable = false, updateable = false, ...props }: Props) => {
  const { data, loading, error, refetch, fetchMore } = useQuery<EventContactsQuery>(
    Query,
    { variables: { eventId, first: pageSize } }
  )
  const {
    pageInfo,
    openNew,
    setOpenNew,
    openEdit,
    setOpenEdit
  } = useTable({ pageInfo: data?.events.nodes[0].eventContacts.pageInfo })
  const [eventContactId, setEventContactId] = useState<string>()

  const editEventContact = (id: string) => {
    if (!updateable) return

    setEventContactId(id)
    setOpenEdit(true)
  }

  if (error) return <Error message={error.message} />
  if (loading || !data) return <Loading />

  return (
    <>
      <BaseTable<EventContactFilters>
        {...props}
        actionButton={createable ? <Button color="green" onClick={() => setOpenNew(true)}>Add</Button> : undefined}
        headings={[{ name: 'Type' }, { name: 'Name' }, { name: 'Email' }, { name: 'Phone' }]}
        moreToFetch={pageInfo.current?.hasNextPage}
        refetch={refetch}
        fetchMore={() => fetchMore({
          variables: { after: pageInfo.current?.endCursor },
          updateQuery(previousData, { fetchMoreResult }) {
            if (Object.keys(previousData).length === 0) return fetchMoreResult
            fetchMoreResult.events.nodes[0].eventContacts.nodes = dedupe([
              ...previousData.events.nodes[0].eventContacts.nodes,
              ...fetchMoreResult.events.nodes[0].eventContacts.nodes
            ])
            return { ...fetchMoreResult }
          }
        })}
        filters={[
          {
            index: 0,
            setValue: (set, value) => set(f => ({ ...f, contactTypeEq: value })),
            type: 'select',
            options: Object.keys(contactTypes).map(ct => ({
              value: ct,
              display: <Badge eventContact={{ name: contactTypes[ct].name, color: contactTypes[ct].color }} />
            })),
          },
          {
            index: 1,
            setValue: (set, value) => set(f => ({ ...f, nameCont: value })),
            type: 'text',
            placeholder: 'What did you call them?'
          },
          {
            index: 2,
            setValue: (set, value) => set(f => ({ ...f, emailCont: value })),
            type: 'text',
            placeholder: 'Did you email them?'
          },
          {
            index: 3,
            setValue: (set, value) => set(f => ({ ...f, phoneCont: value })),
            type: 'text',
            placeholder: 'Did you ring them?'
          },
        ]}
        rows={data.events.nodes[0].eventContacts.nodes.map(eventContact => (
          {
            id: eventContact.id,
            columns: [
              <Badge
                eventContact={{
                  name: contactTypes[eventContact.contactType].name,
                  color: contactTypes[eventContact.contactType].color
                }}
              />,
              eventContact.name,
              eventContact.email,
              eventContact.phone,
            ]
          }
        ))}
        onRowClick={editEventContact}
      />
      { eventContactId && <EditDialog eventId={eventId} id={eventContactId} open={openEdit} setOpen={setOpenEdit} /> }
      <NewDialog eventId={eventId} open={openNew} setOpen={setOpenNew} />
    </>
  )
}
